@if (loading) {
<div class="row">
  <mat-progress-bar mode="indeterminate" class="custom-loader"></mat-progress-bar>
</div>
}

<div class="container-fluid mb-5">
  <!-- Card header + toolbar (come survey-all-responses) -->
  <div class="dashboard-card shadow-sm my-4 border-primary">
    <div class="card-header bg-white border-bottom p-4 card-header-top">
      <div class="row align-items-start">
        <div class="col-md-7">
          <h4 class="dashboard-card-title text-primary-purple mb-1 d-flex align-items-center">
            <fa-icon [icon]="faUsers" class="dashboard-title-icon"></fa-icon>
            {{ 'generic.team' | translate }}
          </h4>
          <p class="text-muted mb-4">Manage participants, track responses, and handle invitations.</p>

          <div class="d-flex stats-row">
            @if (!isPublic) {
            <div class="stat-item mr-5">
              <h2 class="mb-0 font-weight-bold text-primary-purple">{{ members?.length || 0 }}</h2>
              <small class="stat-label">{{ "generic.total-members" | translate }}</small>
            </div>
            }
            <div class="stat-item mr-5">
              <h2 class="mb-0 font-weight-bold text-primary-purple">{{ participantsCount }}</h2>
              <small class="stat-label">{{ "generic.participants" | translate }}</small>
            </div>
            <div class="stat-item mr-5">
              <h2 class="mb-0 font-weight-bold text-success">{{ answersSent?.length }}</h2>
              <small class="stat-label">{{ "components.responses.responses" | translate }}</small>
            </div>
          </div>
        </div>

        @if (chartOptions) {
        <div class="col-md-5 d-flex justify-content-end">
          <div class="chart-wrapper" style="height: 200px; width: 100%; max-width: 280px;">
            <div echarts [options]="chartOptions" style="height: 100%; width: 100%;"></div>
          </div>
        </div>
        }
      </div>
    </div>

    <div class="card-body p-0">
      <div class="row justify-content-center card-details-buttons">
        <div class="col-12">
          <div class="toolbar-filters-inline d-flex flex-wrap align-items-center justify-content-between gap-3 py-2">
            <div class="filters-container">
              <mat-form-field appearance="outline" class="search-field" subscriptSizing="dynamic">
                <fa-icon [icon]="faSearch" matPrefix class="search-icon-prefix mx-3"></fa-icon>
                <input matInput type="text" [name]="'survey_team_search'"
                  [placeholder]="'generic.searchByNameOrEmail' | translate" [(ngModel)]="filters.searchValue"
                  [ngModelOptions]="{standalone: true}" (keyup)="applyFilter()">
              </mat-form-field>
              <mat-form-field appearance="outline" class="filter-field" subscriptSizing="dynamic">
                <mat-label>{{ "components.surveys.survey-team.survey-team.filter-by-answer" | translate }}</mat-label>
                <mat-select name="filter_responseStatus" [(ngModel)]="filters.responseStatus"
                  (selectionChange)="applyFilter()">
                  <mat-option value="ALL">{{ "components.decision-table.new-decision-table.select-framework.all" |
                    translate }}</mat-option>
                  <mat-option value="SENT">{{ "components.surveys.survey-team.survey-team.filter-by-answered" |
                    translate }}</mat-option>
                  <mat-option value="DRAFT">{{ "generic.answers.draft" | translate }}</mat-option>
                  <mat-option value="NOT_SENT">{{ "components.surveys.survey-team.survey-team.filter-by-not-answered" |
                    translate }}</mat-option>
                </mat-select>
              </mat-form-field>
              @if (emailStatuses?.length > 0) {
              <mat-form-field appearance="outline" class="filter-field" subscriptSizing="dynamic">
                <mat-label>{{ "components.surveys.survey-team.survey-team.filter-by-email-status" | translate
                  }}</mat-label>
                <mat-select name="filter_emailStatus" [(ngModel)]="filters.emailStatus"
                  (selectionChange)="applyFilter()" multiple>
                  @for (status of emailStatuses; track status) {
                  <mat-option [value]="status">{{ "generic.email-status." + status + ".title" | translate
                    }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
              }
              @if (roles?.length > 0) {
              <mat-form-field appearance="outline" class="filter-field" subscriptSizing="dynamic">
                <mat-label>{{ "generic.roles.role" | translate }}</mat-label>
                <mat-select name="filter_role" [(ngModel)]="filters.role" (selectionChange)="applyFilter()" multiple>
                  @for (role of roles; track role) {
                  <mat-option [value]="role">{{ role | uppercase }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
              }
              <mat-form-field appearance="outline" class="filter-field" subscriptSizing="dynamic">
                <mat-label>{{ "generic.is-respondent" | translate }}</mat-label>
                <mat-select name="filter_isRespondent" [(ngModel)]="filters.isRespondent"
                  (selectionChange)="applyFilter()" multiple>
                  <mat-option [value]="true">{{ "generic.answers.yes" | translate }}</mat-option>
                  <mat-option [value]="false">{{ "generic.answers.no" | translate }}</mat-option>
                </mat-select>
              </mat-form-field>
            </div>

            <!-- Custom fields filters row -->
            @if (teamMemberCustomFields.length > 0) {
            <div class="custom-filters-toolbar w-100 border-top pt-2 mt-3 mb-4">
              <div class="row g-3">
                @for (field of teamMemberCustomFields; track field.id) {
                <div class="col-md-4">
                  <div class="custom-filter-group">
                    <span class="custom-filter-label">{{ field.name }}</span>

                    @if (field.type === 'text') {
                    <mat-form-field appearance="outline" class="filter-field-full" subscriptSizing="dynamic">
                      <mat-select [name]="'filter_cf_' + field.id" [(ngModel)]="filters.custom[field.id]"
                        (selectionChange)="applyFilter()" multiple>
                        @for (val of getUniqueCustomValues(field); track val) {
                        <mat-option [value]="val">{{ val }}</mat-option>
                        }
                      </mat-select>
                    </mat-form-field>
                    }

                    @if (field.type === 'boolean') {
                    <mat-form-field appearance="outline" class="filter-field-full" subscriptSizing="dynamic">
                      <mat-select [name]="'filter_cf_' + field.id" [(ngModel)]="filters.custom[field.id]"
                        (selectionChange)="applyFilter()">
                        <mat-option value="ALL">Tutti</mat-option>
                        <mat-option value="YES">Sì</mat-option>
                        <mat-option value="NO">No</mat-option>
                      </mat-select>
                    </mat-form-field>
                    }

                    @if (field.type === 'number') {
                    <div class="numeric-filter-range d-flex align-items-center gap-2 w-100">
                      <mat-form-field appearance="outline" class="flex-grow-1" subscriptSizing="dynamic">
                        <mat-label>Min</mat-label>
                        <input matInput type="number" [name]="'filter_cf_min_' + field.id"
                          [(ngModel)]="filters.custom[field.id].min" (keyup)="applyFilter()" (change)="applyFilter()">
                      </mat-form-field>
                      <mat-form-field appearance="outline" class="flex-grow-1" subscriptSizing="dynamic">
                        <mat-label>Max</mat-label>
                        <input matInput type="number" [name]="'filter_cf_max_' + field.id"
                          [(ngModel)]="filters.custom[field.id].max" (keyup)="applyFilter()" (change)="applyFilter()">
                      </mat-form-field>
                    </div>
                    }

                    @if (field.type === 'date') {
                    <div class="numeric-filter-range d-flex align-items-center gap-2 w-100">
                      <mat-form-field appearance="outline" class="flex-grow-1" subscriptSizing="dynamic">
                        <mat-label>Dal</mat-label>
                        <input matInput [matDatepicker]="pickerMin" [name]="'filter_cf_min_' + field.id"
                          [(ngModel)]="filters.custom[field.id].min" (dateChange)="applyFilter()">
                        <mat-datepicker-toggle matSuffix [for]="pickerMin"></mat-datepicker-toggle>
                        <mat-datepicker #pickerMin></mat-datepicker>
                        <mat-hint>DD/MM/YYYY</mat-hint>
                      </mat-form-field>
                      <mat-form-field appearance="outline" class="flex-grow-1" subscriptSizing="dynamic">
                        <mat-label>Al</mat-label>
                        <input matInput [matDatepicker]="pickerMax" [name]="'filter_cf_max_' + field.id"
                          [(ngModel)]="filters.custom[field.id].max" (dateChange)="applyFilter()">
                        <mat-datepicker-toggle matSuffix [for]="pickerMax"></mat-datepicker-toggle>
                        <mat-datepicker #pickerMax></mat-datepicker>
                        <mat-hint>DD/MM/YYYY</mat-hint>
                      </mat-form-field>
                    </div>
                    }
                  </div>
                </div>
                }
              </div>
            </div>
            }
            <div class="toolbar-actions">
              @if (selectedEmailIds.size > 0) {
              <button mat-stroked-button color="primary" (click)="resendSelectedInvitations()">
                <fa-icon [icon]="faBullhorn" class="mr-2"></fa-icon>
                Resend ({{ selectedEmailIds.size }})
              </button>
              <button mat-stroked-button color="warn" (click)="deleteSelectedMembers()">
                <fa-icon [icon]="faTrash" class="mr-2"></fa-icon>
                Remove ({{ selectedEmailIds.size }})
              </button>
              }
              @if (!surveyIsClosed && selectedEmailIds.size == 0) {
              <button mat-stroked-button class="bg-primary-purple text-white border-purple"
                (click)="toggleAddNewTeamMemberMode()">
                <fa-icon [icon]="singleTeamMemberToAddActive ? faTimes : faPlusCircle" class="mr-1"></fa-icon>
                {{ singleTeamMemberToAddActive ? ('generic.cancel' | translate) :
                ('components.decision-table.new-decision-table.compile-proposal.add-team-member' | translate) }}
              </button>
              }
              <button mat-stroked-button class="text-dark-purple border-purple" (click)="openImportModal()">
                <fa-icon [icon]="faFileExcel" class="mr-1"></fa-icon>
                {{ "generic.import" | translate }}
              </button>
              <button mat-stroked-button class="text-dark-purple border-purple" (click)="downloadSurveyTeamDataCSV()">
                <fa-icon [icon]="faDownload" class="mr-1"></fa-icon>
                {{ "generic.export-team-data" | translate }}
              </button>

              <button mat-stroked-button class="text-dark-purple border-purple" [matMenuTriggerFor]="columnMenu">
                <fa-icon [icon]="faColumns" class="mr-1"></fa-icon>
                {{ "generic.columns" | translate }}
              </button>

              <mat-menu #columnMenu="matMenu" class="column-picker-menu">
                <div class="column-picker-header p-3 border-bottom">
                  <h6 class="mb-0 font-weight-bold">{{ "components.surveys.survey-team.survey-team.customize-columns" | translate }}</h6>
                </div>
                <div class="column-picker-body py-2">
                  @for (setting of filteredColumnSettings; track setting.key) {
                  <div class="px-3 py-1 dropdown-item-container" (click)="$event.stopPropagation()">
                    <mat-checkbox color="primary" [checked]="setting.visible" [disabled]="setting.mandatory" (change)="toggleColumn(setting)">
                      <span class="small">{{ setting.label | translate }}</span>
                    </mat-checkbox>
                  </div>
                  }
                </div>
              </mat-menu>
            </div>
          </div>
        </div>
      </div>

      @if (singleTeamMemberToAddActive && selectedEmailIds.size == 0) {
      <div class="bg-light-purple p-4 border-bottom">
        <app-single-team-member [removable]="false" [context]="'survey-team'" [addButton]="true"
          (addedTeamMember)="onAddTeamMember($event)" [surveyId]="surveyId"
          [teamMemberCustomFields]="teamMemberCustomFields" />
      </div>
      }
    </div>
  </div>

  <!-- Tabella separata (come accordion in survey-all-responses) -->
  <div class="team-table-container mt-4">
    <div class="dashboard-card team-table-card">
      <mat-table [dataSource]="dataSource" matSort multiTemplateDataRows class="w-100 custom-table">

        <ng-container matColumnDef="select">
          <mat-header-cell *matHeaderCellDef class="col-select">
            <mat-checkbox (change)="toggleSelectAll($event)" [checked]="isAllSelected()"
              [indeterminate]="isSomeSelected()" color="primary">
            </mat-checkbox>
          </mat-header-cell>
          <mat-cell *matCellDef="let row" class="col-select">
            <mat-checkbox (click)="$event.stopPropagation()" (change)="toggleSelectEmail(row)"
              [checked]="isEmailSelected(row)" color="primary">
            </mat-checkbox>
          </mat-cell>
        </ng-container>

        <ng-container matColumnDef="member_info">
          <mat-header-cell *matHeaderCellDef mat-sort-header class="col-member-info"> {{ 'generic.name' | translate }} /
            {{ 'generic.email' |
            translate }}</mat-header-cell>
          <mat-cell *matCellDef="let row" class="col-member-info">
            <div class="d-flex flex-column py-2">
              <span class="font-weight-bold text-dark">{{ row.name }} {{ row.last_name || '' }}</span>
              <span class="text-muted small">{{ row.email }}</span>
            </div>
          </mat-cell>
        </ng-container>

        <ng-container matColumnDef="role">
          <mat-header-cell *matHeaderCellDef mat-sort-header> {{ 'generic.roles.role' | translate }} </mat-header-cell>
          <mat-cell *matCellDef="let row">
            @if (row.role) {
            <span class="text-muted small font-weight-bold">{{ row.role | uppercase }}</span>
            }
            @if (!row.role) {
            <span class="text-muted small">-</span>
            }
          </mat-cell>
        </ng-container>

        <ng-container matColumnDef="is_respondent">
          <mat-header-cell *matHeaderCellDef class="col-centered"> {{ 'generic.is-respondent' | translate }}
          </mat-header-cell>
          <mat-cell *matCellDef="let row" class="col-centered">
            <span class="status-badge" [class.present]="row.is_respondent !== false"
              [class.absent]="row.is_respondent === false">
              <mat-icon class="status-icon">{{ row.is_respondent !== false ? 'check_circle' : 'remove_circle_outline'
                }}</mat-icon>
              {{ (row.is_respondent !== false ? 'generic.answers.yes' : 'generic.answers.no') | translate }}
            </span>
          </mat-cell>
        </ng-container>

        <ng-container matColumnDef="phone_number">
          <mat-header-cell *matHeaderCellDef class="col-centered"> {{ 'generic.phone' | translate }}
          </mat-header-cell>
          <mat-cell *matCellDef="let row" class="col-centered">
            <span class="text-muted small">{{ row.customs?.phone_number || '-' }}</span>
          </mat-cell>
        </ng-container>

        <ng-container matColumnDef="response_status">
          <mat-header-cell *matHeaderCellDef class="col-centered"> {{ 'generic.response' | translate }}
          </mat-header-cell>
          <mat-cell *matCellDef="let row" class="col-centered">
            <span class="status-badge" [ngClass]="{
                    'present': row.response_status === 'SENT',
                    'draft': row.response_status === 'DRAFT',
                    'absent': row.response_status !== 'SENT' && row.response_status !== 'DRAFT'
                  }">
              <mat-icon class="status-icon">{{
                row.response_status === 'SENT' ? 'check_circle' :
                (row.response_status === 'DRAFT' ? 'edit' : 'remove_circle_outline')
                }}</mat-icon>
              {{ (row.response_status === 'SENT' ? 'generic.answers.yes' :
              (row.response_status === 'DRAFT' ? 'generic.answers.draft' : 'generic.answers.no')) | translate }}
            </span>
          </mat-cell>
        </ng-container>

        <ng-container matColumnDef="email_status">
          <mat-header-cell *matHeaderCellDef class="col-centered"> Email </mat-header-cell>
          <mat-cell *matCellDef="let row" class="col-centered">
            <div class="email-status-indicator"
              [matTooltip]="('generic.email-status.' + (row.last_status_invitation_email | replaceSpaces) + '.title') | translate"
              [style.color]="emailColorByTeamMember(row)" (click)="showEmailResponses(row); $event.stopPropagation()"
              style="cursor: pointer;">
              <fa-icon [icon]="faEnvelope" size="lg"></fa-icon>
            </div>
          </mat-cell>
        </ng-container>

        <ng-container matColumnDef="actions">
          <mat-header-cell *matHeaderCellDef class="col-actions col-centered"> Actions </mat-header-cell>

          <mat-cell *matCellDef="let row" class="col-actions col-end">

            @if (impersonable(row)) {
            <button type="button" class="action-icon-btn text-primary-purple"
              (click)="viewSentQuestionnaire(row); $event.stopPropagation()" matTooltip="View Response">
              <fa-icon [icon]="faEye"></fa-icon>
            </button>
            }

            @if (row.invitation_link && !surveyIsClosed) {
            <button type="button" class="action-icon-btn action-icon-link"
              (click)="copyLink(row.invitation_link); $event.stopPropagation()" matTooltip="Copy Link">
              <fa-icon [icon]="faLink"></fa-icon>
            </button>
            }

            @if (!surveyIsClosed) {
            <button type="button" class="action-icon-btn text-muted" [matMenuTriggerFor]="menu"
              (click)="$event.stopPropagation()">
              <mat-icon>more_vert</mat-icon>
            </button>
            }
            <mat-menu #menu="matMenu">
              <button mat-menu-item (click)="editMemberFromRowMenu(row)">
                <fa-icon [icon]="faEdit" class="mr-2"></fa-icon>
                <span>{{ 'generic.edit' | translate }}</span>
              </button>
              @if (row.is_respondent) {
                <button mat-menu-item class="resend-invite-item" (click)="resendInvitation([row.id])">
                  <fa-icon [icon]="faBullhorn" class="mr-2"></fa-icon>
                  <span>Resend Invite</span>
                </button>
              }
              <button mat-menu-item (click)="deleteTeamMembers([row.id])" class="text-danger">
                <fa-icon [icon]="faTrash" class="mr-2"></fa-icon>
                <span>Remove</span>
              </button>
            </mat-menu>

          </mat-cell>
        </ng-container>

        <!-- Expand column (a destra, come le altre tabelle espandibili) -->
        <ng-container matColumnDef="expand">
          <mat-header-cell *matHeaderCellDef aria-label="row actions" class="expand-column">&nbsp;</mat-header-cell>
          <mat-cell *matCellDef="let element" class="expand-column">
            <button mat-icon-button color="primary" aria-label="expand row"
              (click)="(toggleRow(element)); $event.stopPropagation()">
              <mat-icon>{{ expandedElement === element ? 'expand_less' : 'expand_more' }}</mat-icon>
            </button>
          </mat-cell>
        </ng-container>

        <!-- Dynamic custom field columns -->
        @for (field of teamMemberCustomFields; track field.id) {
        <ng-container [matColumnDef]="'cf_' + field.id">
          <mat-header-cell *matHeaderCellDef>{{ field.name }}</mat-header-cell>
          <mat-cell *matCellDef="let row">
            {{ getCustomFieldValue(row, field) }}
          </mat-cell>
        </ng-container>
        }
        <mat-header-row *matHeaderRowDef="displayedColumns"></mat-header-row>
        <mat-row *matRowDef="let row; columns: displayedColumns;" class="element-row"
          [class.expanded-row]="expandedElement === row" (click)="toggleRow(row); $event.stopPropagation()">
        </mat-row>
        <mat-row *matRowDef="let row; columns: ['expandedDetail']" class="detail-row"
          [class.expanded-detail-row]="expandedElement === row">
        </mat-row>

        <!-- Expanded Content Column -->
        <ng-container matColumnDef="expandedDetail">
          <mat-cell *matCellDef="let element" [attr.colspan]="displayedColumns.length" class="col-detail">
            <div class="element-detail" [@detailExpand]="element == expandedElement ? 'expanded' : 'collapsed'">

              <!-- View Mode: Custom Fields Display -->
              @if (editingMember?.id !== element.id) {
              <div class="detail-content p-2">
                <div class="row custom-fields-grid">
                  <div class="col-md-3 mb-3">
                    <div class="cf-item border-left pl-3 py-1">
                      <div class="cf-label text-muted small uppercase mb-1">{{ 'generic.phone' | translate }}</div>
                      <div class="cf-value font-weight-bold text-dark">
                        {{ element.customs?.phone_number || '-' }}
                      </div>
                    </div>
                  </div>
                  @for (field of teamMemberCustomFields; track field.id) {
                  <div class="col-md-3 mb-3">
                    <div class="cf-item border-left pl-3 py-1">
                      <div class="cf-label text-muted small uppercase mb-1">{{ field.name }}</div>
                      <div class="cf-value font-weight-bold text-dark">
                        {{ getCustomFieldValue(element, field) }}
                      </div>
                    </div>
                  </div>
                  }
                  @if (teamMemberCustomFields.length === 0) {
                  <div class="col-12 py-3 text-center text-muted">
                    {{ "components.surveys.survey-team.survey-team.no-custom-fields" | translate }}
                  </div>
                  }
                </div>
              </div>
              }

              <!-- Edit Mode: Reusable Member and Custom Fields Component -->
              @if (editingMember?.id === element.id) {
              <div class="edit-form-content p-4 bg-light">
                <h6 class="text-primary-purple mb-4 font-weight-bold">{{ 'generic.edit' | translate }}</h6>

                <app-single-team-member [team_member]="editingMember" [teamMemberCustomFields]="teamMemberCustomFields"
                  mode="edit" [surveyId]="surveyId" [loading]="loading" [removable]="false"
                  (onSave)="onSaveMember($event)" (onCancel)="onCancelEdit()">
                </app-single-team-member>
              </div>
              }
            </div>
          </mat-cell>
        </ng-container>

      </mat-table>
    </div>
    <mat-paginator #paginator [pageSizeOptions]="[10, 25, 50]" showFirstLastButtons
      class="mt-5 paginator-rounded"></mat-paginator>
  </div>
</div>
